'use client';
import type { FunctionComponent } from 'react';

import { AbbAttachment } from '@/assets/icons/components/abb-attach-file';
import { useDevice } from '@/modules/utils/custom-hooks/use-device';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Button } from '@/ui-components/button/button';
import { Card } from '@/ui-components/card/card';
import { SearchableDropdown } from '@/ui-components/dropdown/searchable-dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useProjectFilesParams } from './hooks/use-project-files-params';
import { ProjectFilesTable } from './project-files-table';

export const ProjectFiles: FunctionComponent = () => {
  const {
    setFilters,
    t,
    dateOptions,
    isPending,
    project,
    fileInputRef,
    allFilters,
    allProjectFilesSorted,
    toolsForSelect,
    onFileChange,
    handleButtonClick
  } = useProjectFilesParams();
  const { isDarkTheme } = useTheme();
  const { isLaptop } = useDevice();

  return (
    <Flexbox direction='col' className='w-full h-full'>
      <Paragraph variant='text-base-medium' className='mb-2'>
        {!isLaptop && <span>{project?.title ? `${project?.title} ${t('T_FILES')}` : t('T_PROJECT_FILES')}</span>}
      </Paragraph>
      <Card fullWidth fullHeight className='flex-1 dark:bg-card_dark'>
        <Flexbox direction='col'>
          <Flexbox>
            <SearchableDropdown
              onSelect={(value) => !Array.isArray(value) && setFilters((prevState) => ({ ...prevState, documentName: value }))}
              sizing='small'
              options={allFilters ?? []}
              disabled={isPending || !project}
              placeholder={t('T_ALL_DOCUMENTS')}
              onClear={() => setFilters((prevState) => ({ ...prevState, documentName: null }))}
              className='w-full'
            />
          </Flexbox>

          <Flexbox justify='between' items='center' className='flex-wrap mt-2 xx-large:mb-2'>
            <SearchableDropdown
              sizing='small'
              onSelect={(value) =>
                Array.isArray(value) && setFilters((prevState) => ({ ...prevState, startDate: value[0], endDate: value[1] }))
              }
              hasDatePicker
              options={dateOptions}
              disabled={isPending || !project}
              placeholder={t('T_BY_PERIOD')}
              onClear={() => setFilters((prevState) => ({ ...prevState, startDate: null, endDate: null }))}
              className='w-full mb-2 pr-0 xx-large:w-3/6 xx-large:pr-1 xx-large:mb-0'
            />

            <Flexbox className='w-full mb-2 justify-between items-center xx-large:w-3/6 xx-large:mb-0'>
              <SearchableDropdown
                sizing='small'
                onSelect={(value) => !Array.isArray(value) && setFilters((prevState) => ({ ...prevState, documentType: value }))}
                options={toolsForSelect}
                disabled={isPending || !project}
                placeholder={t('T_BY_TOOL')}
                onClear={() => setFilters((prevState) => ({ ...prevState, documentType: null }))}
                className='w-3/5 pr-0 xx-large:w-3/6 xx-large:pr-1 xx-large:mb-0'
              />

              <div className='xx-large:mt-0'>
                <input type='file' onChange={onFileChange} style={{ display: 'none' }} ref={fileInputRef} />
                <Button
                  onClick={handleButtonClick}
                  icon={<AbbAttachment size={20} fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} />}
                  variant='ghost-secondary'
                  sizing='small'
                  className='w-fit text-dark'
                  disabled={!project || isPending}
                >
                  {t('T_ATTACH_FILE')}
                </Button>
              </div>
            </Flexbox>
          </Flexbox>
        </Flexbox>
        <ProjectFilesTable isUploadingPending={isPending} projectFiles={allProjectFilesSorted} />
      </Card>
    </Flexbox>
  );
};
